import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'

import { useTenantBranding } from '../../../contexts/TenantContext'
import useSignatureCanvas, { SIGNATURE_CANVAS_STYLE } from '../../../shared/hooks/useSignatureCanvas'

const API_BASE = import.meta.env.VITE_PROJECTS_API_URL || '/api/projects'
const GONE = 'This signing link is no longer valid. Please ask for a new one.'

/**
 * Where a client signs a lump-sum release. Public: the token in the url is the credential.
 *
 * THIS PAGE DID NOT EXIST, and the backend route it serves has been live for months. Minting a
 * client signing link returned a `sign_path` for an employee to paste into their own email, and
 * every client who followed one landed on the app's bare 404 with a link to a login screen they
 * have no account for. The whole point of that channel is to make "the client signed" distinguish-
 * able from "an employee with edit rights signed as the client" — which it cannot do while the only
 * reachable path is the in-app witnessed one.
 *
 * DELIBERATELY NARROW. The token holder is an external party, so the page shows only what the
 * server's `LumpSumReleasePublicView` returns: the release, the job, the amount. No internal ids,
 * no notes, no counterparty signature.
 *
 * A RELEASE IS THE BILLING EVENT for lump-sum work — signing it releases funds. So the amount is
 * displayed as the largest thing on the page, above the signature, and never below it. Somebody
 * scrolling to a signature box should have passed the number on the way.
 */
export default function ClientReleaseSign() {
  const { slug, token } = useParams()
  const brand = useTenantBranding()
  const base = `${API_BASE}/client/sign-release/${encodeURIComponent(slug)}/${encodeURIComponent(token)}`

  const [release, setRelease] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const [name, setName] = useState('')
  const [signature, setSignature] = useState('')
  const pad = useSignatureCanvas({ value: signature, onChange: setSignature })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(base)
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          // The server's words only when they were written for a person. A service-wide 404 handler
          // can flatten a route's message into "The endpoint /api/v1/... was not found", which in
          // front of a customer leaks an internal path and explains nothing.
          const detail = typeof data.detail === 'string' ? data.detail : ''
          const internal = detail.includes('/api/') || detail.toLowerCase().includes('endpoint')
          throw new Error(!detail || internal ? GONE : detail)
        }
        const data = await res.json()
        if (!cancelled) setRelease(data)
      } catch (e) {
        if (!cancelled) setError(e.message || GONE)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [base])

  const submit = async () => {
    // Checked here so the client is told which box before a round trip, not because the server
    // trusts it — both are required again in `client_sign_by_token`.
    if (!name.trim()) return setError('Please type your name.')
    if (!signature) return setError('Please sign in the box below.')
    setError(null)
    setSubmitting(true)
    try {
      const res = await fetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signer_name: name.trim(), signature }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : GONE)
      setDone(true)
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Shell brand={brand}>
        <div className="flex h-64 items-center justify-center text-sm text-gray-500">Loading…</div>
      </Shell>
    )
  }

  // A unit-price claim is a progress bill, not a release. The server says which (`is_unit_price_claim`
  // on LumpSumReleasePublicView, from the same rule the PDF and certificate use), and a client
  // signing a monthly claim was being asked to "Sign and release" an "Amount released".
  const claim = Boolean(release?.is_unit_price_claim)
  // A MATERIAL release closes a job out on what was SUPPLIED, not built (owner 2026-09-20). Only when
  // it is not a claim - a claim wins, as in the server's own rule.
  const material = !claim && release?.release_kind === 'material'
  const lines = Array.isArray(release?.lines) ? release.lines : []
  // `amount_due` is present only when the job actually holds retainage, which is most jobs' answer
  // of "none". Absent means nothing is held and the claim total IS what gets paid.
  const held = release?.retainage_held ?? null
  const due = release?.amount_due ?? null
  const pct = release?.retainage_pct ?? null
  // `10.00` off the API reads as `10%`. A percentage with trailing zeros looks like a
  // precision nobody agreed to; a job holding 7.5% still shows 7.5%.
  const pctLabel = pct == null || !Number.isFinite(Number(pct))
    ? '' : `${Number(pct).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
  const noun = claim ? 'claim' : 'release'

  if (error && !release) {
    return (
      <Shell brand={brand}>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-700">{error}</p>
        </div>
      </Shell>
    )
  }

  if (done) {
    return (
      <Shell brand={brand}>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" aria-hidden="true" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">Signed — thank you</h1>
          <p className="mt-2 text-sm text-gray-600">
            Your signature on {noun} {release?.release_number} has been recorded. A copy is on its
            way to you.
          </p>
        </div>
      </Shell>
    )
  }

  // `already_signed` is the server's own field, and it is why this page does not simply 404 an
  // opened link: somebody returning to a link they have used deserves to be told it is done, not
  // told it was never real.
  if (release?.already_signed) {
    return (
      <Shell brand={brand}>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">Already signed</h1>
          <p className="mt-2 text-sm text-gray-600">
            {claim ? 'Claim' : 'Release'} {release.release_number} has already been signed. Nothing more is needed from you.
          </p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell brand={brand}>
      <div className="mx-auto max-w-2xl px-4 py-8">
        <h1 className="text-lg font-semibold text-gray-900">{claim ? 'Unit price claim for signature' : material ? 'Material release for signature' : 'Release for signature'}</h1>
        <p className="mt-1 text-sm text-gray-600">
          {release?.project_name}
          {release?.project_number ? ` · ${release.project_number}` : ''}
        </p>

        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-gray-200 bg-white px-4 py-4 sm:grid-cols-2">
          <Row label={claim ? 'Claim #' : 'Release #'} value={release?.release_number} />
          <Row label="Date" value={release?.release_date} />
          {release?.job_name && <Row label="Job" value={release.job_name} />}
          {release?.location_site && <Row label="Location" value={release.location_site} />}
          {release?.po_number && <Row label="PO #" value={release.po_number} />}
        </dl>

        {/* THE DOCUMENT, NOT JUST ITS TOTAL. The consent sentence below says the quantities were
            installed at the contract rates; showing neither and asking somebody to agree to both is
            what the owner called "some half baked screen". These are the same lines the claim PDF
            prints for this same customer. A lump-sum release has none and renders as it always
            did. */}
        {lines.length > 0 && (
          <div className="mt-4 overflow-x-auto rounded-lg border border-gray-300">
            <table className="w-full text-sm" data-testid="claim-lines">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Item</th>
                  <th className="px-3 py-2 text-right font-medium">Qty</th>
                  <th className="px-3 py-2 text-left font-medium">Unit</th>
                  <th className="px-3 py-2 text-right font-medium">Rate</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((li, i) => (
                  <tr key={li.item_no || i} className="border-t border-gray-200">
                    <td className="px-3 py-2 text-gray-900">
                      {li.item_no ? `${li.item_no}. ` : ''}{li.description || ''}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">{qty(li.quantity)}</td>
                    <td className="px-3 py-2 text-gray-500">{li.unit || ''}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">{money(li.unit_rate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">{money(li.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* THE AMOUNT, ABOVE THE SIGNATURE. Signing this release is what releases the funds; the
            number must be passed on the way to the box, not found underneath it.

            WHAT WILL BE PAID, when the job holds retainage. The button used to quote the GROSS
            claim - $21,350.00 on a job holding 10%, when $19,215.00 was the figure that would be
            paid. pay_item_service.retainage_for already says why this belongs on the document: "a
            bill showing the wrong amount due is a bill that does not get paid." */}
        <div className="mt-4 rounded-lg border border-gray-300 bg-gray-50 px-4 py-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {claim ? 'Amount claimed' : 'Amount released'}
          </p>
          <p className="mt-0.5 text-3xl font-semibold tabular-nums text-gray-900">
            {money(release?.total_amount)}
          </p>
          {held != null && (
            <div className="mt-3 border-t border-gray-300 pt-3" data-testid="retainage-block">
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-600">
                    Less retainage held{pctLabel ? ` (${pctLabel})` : ''}
                  </dt>
                  <dd className="tabular-nums text-gray-700">&minus;{money(held)}</dd>
                </div>
                <div className="flex justify-between font-semibold">
                  <dt className="text-gray-900">Amount payable on this claim</dt>
                  <dd className="tabular-nums text-gray-900" data-testid="amount-due">{money(due)}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>
        {claim && (
          <p data-testid="claim-consent" className="mt-3 text-sm text-gray-600">
            Signing confirms that the quantities on this claim were installed during the claim period
            at the contract rates. It does not certify the work complete or close the purchase order.
          </p>
        )}
        {material && (
          <p data-testid="material-consent" className="mt-3 text-sm text-gray-600">
            Signing confirms that the materials described on this release were supplied and accepted,
            and authorises them to be invoiced. It does not certify any work complete.
          </p>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <label className="mt-6 block">
          <span className="text-sm font-medium text-gray-800">
            Your name <span className="text-red-600">*</span>
          </span>
          <input
            type="text"
            value={name}
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <div className="mt-5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-800">
              Sign here <span className="text-red-600">*</span>
            </span>
            <button
              type="button"
              onClick={pad.clear}
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {/* Above the pad, not under it: under the pad is where a thumb rests while the other
                  hand steadies the phone, and a mis-tap there costs the signature. */}
              Clear
            </button>
          </div>
          <div className="relative mt-1.5 rounded-md border border-gray-300 bg-white">
            {!pad.signed && !pad.touched && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-base text-gray-400">
                Sign here
              </div>
            )}
            <canvas
              ref={pad.canvasRef}
              {...pad.handlers}
              aria-label="Signature"
              style={{ ...SIGNATURE_CANVAS_STYLE, height: 170, borderRadius: 6 }}
            />
          </div>
          <p className={`mt-1.5 text-xs ${pad.signed ? 'text-green-600' : 'text-gray-500'}`}>
            {pad.signed ? 'Signed' : 'Not signed yet'}
          </p>
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {submitting ? 'Signing…' : `${claim ? 'Sign claim for' : 'Sign and release'} ${money(due ?? release?.total_amount)}`}
        </button>

        <p className="mt-5 text-xs text-gray-500">
          This link works once. Nothing is recorded until you press the button above.
        </p>
      </div>
    </Shell>
  )
}

function qty(value) {
  // Four decimals, trailing zeros trimmed. A claim's quantities are agreed to that precision
  // (pay items store four), and printing 400.0000 where somebody wrote 400 reads as a system
  // showing off rather than a bill.
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 })
}

function money(value) {
  const amount = Number(value)
  // A release with no amount is a data problem, not a free release — say nothing rather than "$0".
  if (!Number.isFinite(amount)) return ''
  return amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function Row({ label, value }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
    </div>
  )
}

function Shell({ brand, children }) {
  return (
    <div className="min-h-screen bg-gray-50">
      {brand?.company_name && (
        <div className="border-b border-gray-200 bg-white px-4 py-2">
          <p className="text-sm font-medium text-gray-600">{brand.company_name}</p>
        </div>
      )}
      {children}
    </div>
  )
}
